import { Map } from 'immutable'
import { connect } from 'react-redux'
import ImmutablePropTypes from 'react-immutable-proptypes'
import PropTypes from 'prop-types'
import React from 'react'
import debounce from 'lodash/debounce'
import memoize from 'lodash/memoize'

import { CartContext } from './CartContext'
import {
  clearCartCouponError,
  redeemCoupon,
  removeCoupon,
  setCartBusyState,
  setCartQuantities,
  updateLineItemQuantity,
} from '../../../store/actions'
import { sendCartInteraction } from '../../../utils/googleAnalytics'
import { track } from '../../../utils/tracking'
import createPromiseChunker from '../../../utils/createPromiseChunker'

export class CartRaw extends React.Component {
  static propTypes = {
    cart: PropTypes.object.isRequired,
    children: PropTypes.node.isRequired,
    quantities: PropTypes.object.isRequired,
    redeemCoupon: PropTypes.func.isRequired,
    clearCouponError: PropTypes.func.isRequired,
    removeCoupon: PropTypes.func.isRequired,
    setQuantities: PropTypes.func.isRequired,
    updateLineItemQuantity: PropTypes.func.isRequired,
    isCartBusy: PropTypes.bool,
    location: ImmutablePropTypes.map.isRequired,
    isBeyond: PropTypes.bool.isRequired,
  }

  shouldComponentUpdate(nextProps) {
    return !nextProps.isCartBusy
  }

  getQuantity = (lineItem) => {
    return this.props.quantities[lineItem.lineItemId] ? this.props.quantities[lineItem.lineItemId] : lineItem.quantity
  }

  setQuantity = (lineItem, quantity, quantityΔ) => {
    if ((this.getQuantity(lineItem) === quantity && quantity > 0) || this.props.isCartBusy) return Promise.resolve()

    this.props.setQuantities({
      ...this.props.quantities,
      [lineItem.lineItemId]: quantity,
    })

    return this.props
      .updateLineItemQuantity(lineItem.lineItemId, lineItem.productId, quantity, quantityΔ)
      .then(() => sendCartInteraction(lineItem, quantityΔ))
      .then(() => track('cart:setQuantity', { lineItem, quantity, quantityDelta: quantityΔ }))
  }

  redeemCoupon(couponCode) {
    this.props.clearCouponError()
    return this.props.redeemCoupon(couponCode)
  }

  removeCoupon() {
    return this.props.removeCoupon()
  }

  /**
   * Checks if the cart has an invalid coupon
   *
   * @returns {boolean} Whether the cart has an invalid coupon or not
   */
  hasInvalidCoupon() {
    const { cart } = this.props

    return cart.coupon ? cart.coupon.validationErrors.length > 0 : false
  }

  render() {
    return <CartContext.Provider value={this}>{this.props.children}</CartContext.Provider>
  }
}

function makeDispatchToProps() {
  return (dispatch) => {
    // Used to ensure that the cart stays in "busy" state when the quantity of
    // a line item is about to be updated via debounced function invocation.
    const lineItemsAwaitingQuantityUpdate = new Set()

    const promiseChunker = createPromiseChunker((transition) => {
      if (transition === 'start') {
        dispatch(setCartBusyState(true))
      } else if (transition === 'end' && lineItemsAwaitingQuantityUpdate.size === 0) {
        dispatch(setCartBusyState(false))
      }
    })

    const updateLineItemQuantityDebounced = memoize((lineItemId, productId) =>
      debounce(
        (quantity, quantityΔ) => {
          lineItemsAwaitingQuantityUpdate.delete(lineItemId)
          return promiseChunker(() => dispatch(updateLineItemQuantity(lineItemId, productId, quantity, quantityΔ)))
        },
        1000,
        {
          leading: true,
          trailing: true,
        },
      ),
    )

    return {
      setQuantities: (quantities) => dispatch(setCartQuantities(quantities)),
      updateLineItemQuantity: (lineItemId, productId, quantity, quantityΔ) => {
        lineItemsAwaitingQuantityUpdate.add(lineItemId)
        return updateLineItemQuantityDebounced(lineItemId, productId)(quantity, quantityΔ)
      },
      redeemCoupon: (couponCode) => promiseChunker(() => dispatch(redeemCoupon(couponCode))),
      clearCouponError: () => dispatch(clearCartCouponError()),
      removeCoupon: () => promiseChunker(() => dispatch(removeCoupon())),
    }
  }
}

export default connect(
  (state) => ({
    isBeyond: Boolean(state.getIn(['shop', 'beyond'])),
    location: state.get('location'),
    cart: state.get('cart').toJS(),
    isCartBusy: state.getIn(['view', 'cart', 'isBusy']),
    quantities: state.getIn(['view', 'cart', 'quantities'], new Map()).toJS(),
  }),
  makeDispatchToProps,
  null,
  { pure: false },
)(CartRaw)
